// D3 Line Chart for BodyOS data visualizations
import * as d3 from 'd3'
import {
  colors,
  fonts,
  animationConfig,
  defaultMargins,
  defaultDimensions
} from './config'
import {
  createSvg,
  createXAxis,
  createYAxis,
  createTooltip,
  showTooltipHtml,
  hideTooltip,
  createLegend,
  createGradient,
  debounce
} from './utils'

export default class LineChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container
    this.options = {
      width: defaultDimensions.width,
      height: defaultDimensions.height,
      margins: { ...defaultMargins },
      xLabel: '',
      yLabel: '',
      showArea: false,
      showPoints: true,
      showLegend: true,
      animate: true,
      responsive: true,
      ...options
    }

    this.data = []
    this.svg = null
    this.tooltip = null

    if (this.options.responsive) {
      this.handleResize = debounce(() => this.render(), 250)
      window.addEventListener('resize', this.handleResize)
    }
  }

  setData(data) {
    this.data = data
    return this
  }

  render() {
    // Clear existing content
    this.container.innerHTML = ''

    if (!this.data || this.data.length === 0) {
      this.renderEmptyState()
      return this
    }

    // Get responsive dimensions
    const containerWidth = this.container.clientWidth || this.options.width
    const width = Math.min(containerWidth, this.options.width)
    const height = (width / this.options.width) * this.options.height

    // Create SVG
    const { svg, g, innerWidth, innerHeight } = createSvg(
      this.container,
      width,
      height,
      this.options.margins
    )
    this.svg = svg

    // Create tooltip
    this.tooltip = createTooltip(this.container)

    // Prepare data - handle single or multi-series
    const series = Array.isArray(this.data[0]?.values) ? this.data : [{ name: 'Data', values: this.data }]

    // Create scales
    const allValues = series.flatMap((s) => s.values)
    const xScale = d3.scalePoint()
      .domain(allValues.map((d) => d.x))
      .range([0, innerWidth])
      .padding(0.5)

    const yMin = d3.min(allValues, (d) => d.y)
    const yMax = d3.max(allValues, (d) => d.y)
    const yPadding = (yMax - yMin) * 0.1
    const yScale = d3.scaleLinear()
      .domain([Math.max(0, yMin - yPadding), yMax + yPadding])
      .range([innerHeight, 0])
      .nice()

    // Create axes
    createXAxis(g, xScale, innerHeight, {
      label: this.options.xLabel,
      rotate: this.options.rotateXLabels ? -45 : 0
    })
    createYAxis(g, yScale, {
      label: this.options.yLabel,
      gridLines: true,
      width: innerWidth,
      tickFormat: this.options.yTickFormat
    })

    // Create line generator
    const line = d3.line()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y))
      .curve(d3.curveLinear) // Sharp lines for isometric aesthetic

    // Draw each series
    series.forEach((s, i) => {
      const seriesColor = s.color || colors.chart[i % colors.chart.length]

      // Area fill
      if (this.options.showArea) {
        const gradientId = `area-gradient-${i}`
        const gradientUrl = createGradient(svg, gradientId, seriesColor, 0.2, 0)

        const area = d3.area()
          .x((d) => xScale(d.x))
          .y0(innerHeight)
          .y1((d) => yScale(d.y))
          .curve(d3.curveLinear)

        g.append('path')
          .datum(s.values)
          .attr('class', 'area')
          .attr('fill', gradientUrl)
          .attr('d', area)
      }

      // Line
      const path = g.append('path')
        .datum(s.values)
        .attr('class', 'line')
        .attr('fill', 'none')
        .attr('stroke', seriesColor)
        .attr('stroke-width', 2)
        .attr('d', line)

      // Animate line drawing
      if (this.options.animate) {
        const totalLength = path.node().getTotalLength()
        path
          .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
          .attr('stroke-dashoffset', totalLength)
          .transition()
          .duration(animationConfig.duration)
          .ease(d3.easeCubicOut)
          .attr('stroke-dashoffset', 0)
      }

      // Points
      if (this.options.showPoints) {
        const points = g.selectAll(`.point-${i}`)
          .data(s.values)
          .enter()
          .append('circle')
          .attr('class', `point point-${i}`)
          .attr('cx', (d) => xScale(d.x))
          .attr('cy', (d) => yScale(d.y))
          .attr('r', 0)
          .attr('fill', colors.neutral.white)
          .attr('stroke', seriesColor)
          .attr('stroke-width', 2)
          .style('cursor', 'pointer')

        // Animate points
        if (this.options.animate) {
          points.transition()
            .delay((d, j) => animationConfig.duration + j * 50)
            .duration(300)
            .attr('r', 4)
        } else {
          points.attr('r', 4)
        }

        // Tooltip interactions
        points
          .on('mouseover', (event, d) => {
            d3.select(event.target)
              .transition()
              .duration(200)
              .attr('r', 6)

            const content = this.formatTooltip(d, s.name)
            showTooltipHtml(this.tooltip, content, event)
          })
          .on('mouseout', (event) => {
            d3.select(event.target)
              .transition()
              .duration(200)
              .attr('r', 4)
            hideTooltip(this.tooltip)
          })
      }
    })

    // Legend
    if (this.options.showLegend && series.length > 1) {
      const legendItems = series.map((s, i) => ({
        label: s.name,
        color: s.color || colors.chart[i % colors.chart.length]
      }))
      createLegend(g, legendItems, { x: 0, y: -25 })
    }

    return this
  }

  formatTooltip(d, seriesName) {
    const value = this.options.formatValue ? this.options.formatValue(d.y) : d.y
    let html = `<strong>${d.x}</strong><br/>`
    if (seriesName && seriesName !== 'Data') {
      html += `${seriesName}: `
    }
    html += `<span style="color: ${colors.accent}">${value}</span>`
    if (d.label) {
      html += `<br/><small style="color: ${colors.textLight}">${d.label}</small>`
    }
    return html
  }

  renderEmptyState() {
    const emptyDiv = document.createElement('div')
    emptyDiv.className = 'd3-empty-state'
    emptyDiv.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        border: 2px dashed ${colors.neutral[300]};
        color: ${colors.textMuted};
        font-family: ${fonts.family};
        font-size: ${fonts.sizes.base}px;
      ">
        NO DATA AVAILABLE
      </div>
    `
    this.container.appendChild(emptyDiv)
  }

  destroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    if (this.container) {
      this.container.innerHTML = ''
    }
  }
}
